import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { FAQ } from "@/constants/testIds";

const ITEMS = [
  {
    q: "How is the Wishire Score calculated?",
    a: "It's a composite of five signals — resume quality, verified skills, voice interview performance, career trajectory and communication — weighted by the target role. Every input is explainable: you can see exactly what moved the number.",
  },
  {
    q: "Is the voice AI interview really fair?",
    a: "Yes. We audit for bias across accents, gender and educational background quarterly. The AI scores structure, depth and clarity — not pitch, accent or confidence theatre. Every score is reviewable by a human.",
  },
  {
    q: "Do students pay to use Wishire?",
    a: "The core experience — roadmap, drives, score, free practice sessions — is covered by the college. Optional practice packs unlock extra AI interviews and proctored tests for students who want more reps.",
  },
  {
    q: "How hard is it for a college to onboard?",
    a: "One working session. Bulk-import your student list, share the invite code, add your logo — your placement cell is live the same day, with your branding on every student dashboard and report.",
  },
  {
    q: "When does the platform launch?",
    a: "We're onboarding a founding cohort of colleges for the upcoming placement season — pilot one department first, scale on proof. Book a demo to reserve your college's slot.",
  },
  {
    q: "Will student data be private?",
    a: "Yes. Student data belongs to the student and their college. Scores and transcripts are visible to the placement cell for readiness tracking; nothing is ever sold or shared with third parties.",
  },
];

export default function FaqSection() {
  const [open, setOpen] = useState(0);

  return (
    <section className="section">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Left column */}
          <div className="md:col-span-4">
            <span className="overline">FAQ</span>
            <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
              Honest answers.{" "}
              <span className="italic text-stone-500">No marketing-speak.</span>
            </h2>
            <p className="mt-5 text-stone-600">
              Didn&apos;t find what you were looking for?{" "}
              <a
                href="#waitlist"
                className="underline underline-offset-4 hover:text-[#C2410C] transition-colors"
              >
                Drop your email
              </a>{" "}
              and we&apos;ll reply within a day.
            </p>
          </div>

          {/* Right column — accordion */}
          <div className="md:col-span-8">
            <ul className="border-t border-stone-200">
              {ITEMS.map((it, i) => {
                const isOpen = open === i;
                return (
                  <li key={i} className="border-b border-stone-200">
                    <motion.button
                      data-testid={FAQ(i)}
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      className="w-full flex items-center justify-between py-6 text-left group relative"
                      whileHover="hovered"
                      initial="rest"
                      animate="rest"
                    >
                      {/* Terracotta left border flash on hover */}
                      <motion.span
                        variants={{
                          rest: { scaleY: 0, opacity: 0 },
                          hovered: { scaleY: 1, opacity: 1 },
                        }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-0 top-4 bottom-4 w-0.5 bg-[#C2410C] rounded-full origin-center"
                      />

                      <span className="font-display text-xl md:text-2xl pr-8 relative">
                        {/* Hover underline slide-in */}
                        <span className="relative inline">
                          {it.q}
                          <motion.span
                            variants={{
                              rest: { scaleX: 0 },
                              hovered: { scaleX: 1 },
                            }}
                            transition={{ duration: 0.25, ease: "easeOut" }}
                            className="absolute -bottom-0.5 left-0 right-0 h-px bg-[#C2410C] origin-left"
                          />
                        </span>
                      </span>

                      <motion.span
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="shrink-0 text-stone-500"
                      >
                        <ChevronDown size={20} />
                      </motion.span>
                    </motion.button>

                    {/* AnimatePresence accordion body */}
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          key="content"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                          className="overflow-hidden"
                        >
                          <p className="pb-6 text-stone-600 leading-relaxed max-w-2xl">
                            {it.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
